import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LocaleProvider, useLocale, SubscriptionProvider, Paywall, useOrganizationId, NotFoundPage } from '@platform/core';
import { SimpleAuthProvider, useSimpleAuth } from './contexts/SimpleAuthContext';
import { BudgetProvider, useBudget } from './contexts/BudgetContext';
import { DashboardPage } from './pages/DashboardPage';
import { BudgetDetailPage } from './pages/BudgetDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { DataEntryPage } from './pages/DataEntryPage';
import { ChartsPage } from './pages/ChartsPage';
import { LoginPage } from './pages/LoginPage';
import { Layout } from './components/Layout';

// Main app content (protected)
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useSimpleAuth();
  const { t } = useLocale();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated - show main app
  return (
    <BudgetProvider>
      <AppRoutes />
    </BudgetProvider>
  );
};

// Separate component for routes that uses BudgetProvider
const AppRoutes: React.FC = () => {
  const { isLoading, companies } = useBudget();
  const { t } = useLocale();

  // Show loading while loading companies
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  // No companies found - show message
  if (companies.length === 0) {
    return (
      <div className="loading-screen">
        <div className="no-company-message">
          <h2>{t('errors.noOrganization')}</h2>
          <p>{t('errors.contactAdmin')}</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/data" element={<DataEntryPage />} />
        <Route path="/charts" element={<ChartsPage />} />
        <Route path="/budget/:budgetId" element={<BudgetDetailPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
};

function AppContentWithPaywall() {
  const { organizationId, isAdmin } = useOrganizationId();

  return (
    <SubscriptionProvider organizationId={organizationId}>
      <Paywall isAdmin={isAdmin}>
        <AppContent />
      </Paywall>
    </SubscriptionProvider>
  );
}

function App() {
  return (
    <LocaleProvider>
      <BrowserRouter>
        <SimpleAuthProvider>
          <AppContentWithPaywall />
        </SimpleAuthProvider>
      </BrowserRouter>
    </LocaleProvider>
  );
}

export default App;
